import { createState } from "@/global/store/zustand_store";
import { NotificationModal } from "@/shared/common/InfoPopup/NotificationModal";
import { Box, Grid, Stack, useTheme } from "@mui/material";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { EventType } from "@/global/store/data/types";
import { useAppStore } from "@/global/store";
import {
  selectEvents,
  selectEventsSelected,
} from "@/global/store/data/purchaseSeats/events";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import React from "react";
import { throttleTime } from "@/global/utils/utils";
import { NoInventoryEventModalResponse } from "./NoInventoryAlertModal";
import { ModalActionButton } from "@/shared/base/components/ModalActionButton/ModalActionButton";
import { selectPackageSelected } from "@/global/store/data/purchaseSeats/packages";
import { ModalTitleText } from "@/shared/base/components/ModalReusables/ModalReusables";
import { ContinueWithoutSelectionButton } from "./force-event-selection-common";
import { CombinedEventCard } from "@/shared/common/EventCard/CombinedEventCard";

export const useChooseMissingPackageEventsModal = createState({
  isOpen: false,
  eventCode: "",
  selectedEvents: [] as EventType[],
  onFinish: (a: NoInventoryEventModalResponse) => {},
  isRetryDisabled: false as boolean,
});

export function ChooseMissingPackageEventsModal() {
  const theme = useTheme();
  const { md: mdDown } = useCssMediaQuery("down");
  const teamConfig = useTeamConfig();
  const modalState = useChooseMissingPackageEventsModal();
  const globalSelectedEvents = useAppStore(selectEventsSelected);
  const selectedPackage = useAppStore(selectPackageSelected);
  const events = useAppStore(selectEvents);

  const eventsToDisplay = events.filter((evt) => {
    const eventCodes = globalSelectedEvents.map((itm) => itm.eventCode);
    return (
      !eventCodes.includes(evt.eventCode) &&
      evt.eventCode !== modalState?.eventCode
    );
  });

  const handleSelectEvent = (event: EventType) => {
    const isSelected = modalState.selectedEvents.some((evt) => {
      return evt.eventCode === event.eventCode;
    });
    let selectedEvents = [...modalState.selectedEvents];
    if (isSelected) {
      selectedEvents = selectedEvents.filter((evt) => {
        return evt.eventCode !== event.eventCode;
      });
    } else {
      selectedEvents.push(event);
    }
    modalState.set({ selectedEvents });
  };

  const onClickConfirm = () => {
    modalState.onFinish({
      action: "confirmedGameSelection",
      selectedEvents: modalState.selectedEvents,
    });
    handleClose();
  };

  const handleClose = () => {
    modalState.set({ isOpen: false });
    throttleTime(400, () => {
      modalState.reset();
    });
  };

  const onClickBackToFindGames = () => {
    modalState.onFinish({ action: "backToFindGames" });
    handleClose();
  };

  const onClickContinue = () => {
    modalState.onFinish({ action: "continue" });
    handleClose();
  };

  const onClickTryAgain = () => {
    modalState.onFinish({ action: "tryAgain" });
    handleClose();
  };

  return (
    <NotificationModal
      isOpen={modalState.isOpen}
      width="lg"
      hideTitleIcon
      hideCloseIcon={modalState?.isRetryDisabled}
      dialogProps={{ fullScreen: mdDown ? true : false }}
      bodyProps={{
        hideTopDivider: true,
        hideBottomDivider: true,
        style: { maxHeight: mdDown ? "unset" : "700px" },
      }}
      renderHeader={() => {
        return (
          <Box
            py={2}
            px={3}
            sx={{
              backgroundColor: theme.palette.background.paper,
              position: "sticky",
              top: 0,
              zIndex: 999,
              borderBottom: `1px solid ${theme.palette.divider}`,
              width: "100%",
            }}
          >
            <ModalTitleText>Choose Another Event</ModalTitleText>
          </Box>
        );
      }}
      renderBody={() => {
        return (
          <Box position={"relative"}>
            <Box p={mdDown ? 2 : 3} pb={28}>
              <Grid container spacing={4}>
                {eventsToDisplay.map((event: EventType) => {
                  const isSelected = modalState.selectedEvents.some(
                    (ev: EventType) => ev.eventCode === event.eventCode
                  );
                  return (
                    <Grid key={event.eventCode} item xs={12} md={6} lg={4}>
                      <CombinedEventCard
                        variant="upcoming"
                        event={event}
                        // isDisabled={!isSelected}
                        isSelected={isSelected}
                        onSelect={() => handleSelectEvent(event)}
                        isInvPreviewButtonHidden={true}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
            <Stack
              direction={mdDown ? "column" : "row"}
              spacing={2}
              p={2}
              justifyContent={"flex-end"}
              sx={{
                backgroundColor: theme.palette.background.paper,
                position: mdDown ? "fixed" : "sticky",
                bottom: 0,
                zIndex: 3,
                borderTop: `1px solid ${theme.palette.divider}`,
                width: "100%",
              }}
            >
              <Stack
                direction={"row"}
                spacing={2}
                width={mdDown ? "100%" : "auto"}
              >
                {!modalState?.isRetryDisabled && (
                  <ModalActionButton
                    variant="outlined"
                    onClick={onClickTryAgain}
                    sx={{ width: mdDown ? "100%" : "auto" }}
                  >
                    Retry
                  </ModalActionButton>
                )}

                <ContinueWithoutSelectionButton
                  eventCode={modalState.eventCode}
                  onClickContinue={onClickContinue}
                  onClickBackToFindGames={onClickBackToFindGames}
                  buttonProps={{ sx: { width: mdDown ? "100%" : "auto" } }}
                />
              </Stack>

              <ModalActionButton
                isDisabled={modalState.selectedEvents.length <= 0}
                onClick={onClickConfirm}
              >
                Confirm Game Selection
              </ModalActionButton>
            </Stack>
          </Box>
        );
      }}
      renderFooter={() => {
        return <></>;
      }}
    />
  );
}
